import { List, TextField, DateField, NumberField, TextInput, NumberInput, useDataProvider, useListContext, sanitizeListRestProps, TopToolbar } from 'react-admin';
import Datagrid from '../Commons/Datagrid';
import PostPagination from '../Commons/PostPagination';
import Button from '@mui/material/Button';
import * as XLSX from 'xlsx';
import { useMediaQuery, createTheme } from '@mui/material';
import BreadCrumbCustom from '../Commons/BreadCrum';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import historyTracker from '../Commons/historyTracker';
import AuthNotification from '../Commons/AuthNotification';
import { StyleTheme, Style } from '../Commons/Styles.jsx';
import { createStyles, makeStyles } from '@mui/styles';
import FilterSidebar from '../Commons/FilterSidebar';
import InternalMenu from '../Commons/InternalMenu';
import CircularProgress from '@mui/material/CircularProgress';
// TODO: reemplazar por el ícono definitivo de Proveedores cuando esté disponible
import providers from '../Commons/Icons/apps.svg';
import providerList from '../Commons/Icons/book.svg';

import { hasPermissions } from '../Commons/CompanyAuth';

// El ListActions compartido arma el link de "Crear" como href="#/{resource}/create"
// (una navegacion plana, sin state), pero esta pantalla necesita llevar el
// codigo_proveedor al formulario de creacion, asi que usa un TopToolbar propio
// que navega con history.push (state + query) en su lugar. El boton "Exportar"
// se reimplementa aca igual (misma logica que Commons/ListActions.jsx: trae hasta
// 1000 registros con el filtro/orden actual via dataProvider.getList y arma un
// .xlsx en el navegador) porque este TopToolbar propio reemplaza por completo al
// compartido, no lo envuelve.
const InvoiceListActions = (props) => {
  const { create, onCreate, download, ...rest } = props;
  const { sort, resource, filterValues } = useListContext();
  const dataProvider = useDataProvider();
  const theme = createTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  async function exportToExcel(filename, resource, sort, filterValues) {
    let data = await dataProvider.getList(resource, { pagination: { page: 1, perPage: 1000 }, sort, filter: filterValues });
    let columns = [];
    for (let key in data.data[0]) {
      columns.push(key)
    }
    const ws = XLSX.utils.json_to_sheet(data.data, { header: columns });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1');
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  return (
    <TopToolbar {...sanitizeListRestProps(rest)}>
      {!isSmall && download ? (
        <Button
          onClick={() => exportToExcel('data', resource, sort, filterValues)}
          style={Style.ToolBarButton}>
          Exportar
        </Button>
      ) : null}
      {create ? (
        <Button onClick={onCreate} color="primary" style={Style.ToolBarButton}>
          Crear
        </Button>
      ) : null}
    </TopToolbar>
  );
};

// El filtro ya viaja codificado en la URL como ?filter={"provider":"..."} (asi es como
// se abre esta pantalla desde ProvidersShow), asi que se reutiliza como fuente de verdad
// para saber a que proveedor esta acotado el listado.
// IMPORTANTE: no se puede confiar en location.state aqui. <List syncWithLocation> (mas
// abajo) reescribe la URL apenas monta para sincronizar filtro/orden/paginacion, y esa
// reescritura pierde el location.state original de history.push. Por eso tanto el
// codigo del proveedor como su nombre viajan en la propia query string, no en state.
const getProviderCodeFromFilterQuery = (search) => {
  try {
    const params = new URLSearchParams(search || '');
    const filterQuery = params.get('filter');
    if (!filterQuery) return null;
    const parsed = JSON.parse(filterQuery);
    return parsed?.provider ?? null;
  } catch {
    return null;
  }
};

const getProviderNameFromQuery = (search) => {
  try {
    const params = new URLSearchParams(search || '');
    return params.get('providerName') || null;
  } catch {
    return null;
  }
};

// Listado de facturas de un proveedor puntual (sp_lista_cbfactura_editable acotado por
// codigo_proveedor). Se llega aqui desde el botón "Por Procesar" de ProvidersShow, que ya
// deja el filtro del proveedor codificado en la URL (?filter={"provider":"..."}) y su
// nombre en ?providerName= para precargarlo al crear una factura nueva (handleCreateInvoice).
// El breadcrumb en si usa una etiqueta fija ("Facturas por Procesar"): al ser un listado,
// no un registro puntual, no tiene sentido sufijarlo con el nombre del proveedor.
const useStyles = makeStyles(() => createStyles(StyleTheme));

// invoiceAmountFrom/invoiceAmountTo no son columnas del SP (sp_lista_cbfactura_editable
// no soporta filtrar por monto), asi que igual que en ProviderPayableInvoicesList se
// filtran en memoria en el propio endpoint (Get_Provider_Invoices), antes de paginar.
const PostFilter = [
  <TextInput InputProps={{ disableUnderline: true }} label={<span>{"No. Factura"}</span>} source="invoiceNumber" alwaysOn />,
  <NumberInput InputProps={{ disableUnderline: true }} label={<span>{"Monto Desde"}</span>} source="invoiceAmountFrom" alwaysOn />,
  <NumberInput InputProps={{ disableUnderline: true }} label={<span>{"Monto Hasta"}</span>} source="invoiceAmountTo" alwaysOn />,
];

const ProviderInvoicesList = (props) => {
  const classes = useStyles();
  const theme = createTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [openAuth, setOpenAuth] = useState(false);
  const [auth, setAuth] = useState(false);
  const [authCreate, setAuthCreate] = useState(false);
  // Mientras esto es true se muestra el spinner de carga inicial (ver el bloque
  // "initialLoading" en el return): sin este estado la ventana se veia en blanco
  // (solo el breadcrumb) mientras se traian los permisos.
  const [initialLoading, setInitialLoading] = useState(true);
  const dataProvider = useDataProvider();
  const providerName = getProviderNameFromQuery(location.search) || location.state?.providerName;
  const providerCode = getProviderCodeFromFilterQuery(location.search) || location.state?.providerCode;

  useEffect(() => {
    (async () => {
      try {
        const currentPath = location.pathname;
        historyTracker.addPath(currentPath, 'Facturas por Procesar', 4);

        const authdata = { data: hasPermissions('ProviderInvoice', 'list,create') };
        if (authdata.data.length > 0) {
          setAuth(authdata.data[0]);
          setOpenAuth(!authdata.data[0]);
          setAuthCreate(authdata.data[1]);
        } else {
          setAuth(false);
          setOpenAuth(true)
        }
      }
      catch {
      }
      finally {
        setInitialLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProvider, location.pathname]);

  const handleCreateInvoice = () => {
    navigate({
      pathname: '/provider/invoice/create',
      search: `?provider=${encodeURIComponent(providerCode)}&providerName=${encodeURIComponent(providerName || '')}`,
    }, { state: { providerName, providerCode } });
  };

  const handleRedirectProviderList = () => {
    navigate(`/provider`);
  };

  const [openProviders, setOpenProviders] = useState(true);

  const handleClickOpenProviders = () => {
    setOpenProviders(!openProviders);
  };

  const menus = [{
    icon: <img src={providers} alt="logo" style={Style.MenuLogo} />, name: 'Proveedores',
    open: openProviders,
    openAction: handleClickOpenProviders,
    actions: [
      { icon: <img src={providerList} alt="logo" style={Style.MenuLogo} />, name: 'Listado', onClick: handleRedirectProviderList },
    ]
  }];

  let pagination = <PostPagination menu={<InternalMenu menus={menus} />} />;

  return (
    <div class="container">
      <BreadCrumbCustom history={historyTracker.getHistory()} />
      {initialLoading ? (
        // Spinner de carga inicial: reutiliza la misma clase "Loading" (index.css) y
        // el mismo CircularProgress que ya se usa en otras pantallas para el
        // guardado, pero aqui cubre la espera de auth, que antes dejaba la
        // pantalla en blanco.
        <div class="Loading" style={{ display: 'block' }}>
          <CircularProgress size="15rem" />
        </div>
      ) : auth ? (
        <div class="row">
          <div class="col-12">
            {isSmall ? (
              <List empty={false} {...props} actions={<InvoiceListActions create={authCreate} onCreate={handleCreateInvoice} />} bulkActionButtons={false} pagination={pagination}>
                <Datagrid {...props} rowClick="show">
                </Datagrid>
              </List>
            ) : (
              <List empty={false} perPage={10} {...props} aside={<FilterSidebar {...props} filters={PostFilter} />} actions={<InvoiceListActions create={authCreate} onCreate={handleCreateInvoice} download />} bulkActionButtons={false} pagination={pagination}>
                <Datagrid {...props} rowClick="show" style={Style.DataGrid}>
                  <TextField source="invoiceNumber" style={Style.fieldList} label="No. Factura" cellClassName={classes.fields} />
                  <DateField source="paymentDate" style={Style.fieldList} label="Fecha de Pago" cellClassName={classes.fields} />
                  <NumberField source="invoiceAmount" style={Style.fieldList} label="Monto" cellClassName={classes.fields} options={{ style: 'currency', currency: 'GTQ' }} />
                  <NumberField source="vatAmount" style={Style.fieldList} label="IVA" cellClassName={classes.fields} options={{ style: 'currency', currency: 'GTQ' }} />
                  <NumberField source="paidAmount" style={Style.fieldList} label="Abonado" cellClassName={classes.fields} options={{ style: 'currency', currency: 'GTQ' }} />
                  <TextField source="concept" style={Style.fieldList} label="Concepto" cellClassName={classes.fields} />
                </Datagrid>
              </List>
            )}
          </div>
        </div>) : (<AuthNotification openerror={openAuth} severity={'error'} message={'No posee los permisos para poder interactuar con esta vista'} />)}
    </div>
  )
};

export default ProviderInvoicesList;
